import React, { useState, useEffect } from 'react';
import { Card } from '../ui';
import { ListTodo, Clock, CheckCircle, XCircle, RefreshCw, Calendar, User } from 'lucide-react';
import { scrapingService } from '../../services/scrapingService';
import useSocialMediaStore from '../../stores/socialMediaStore';
import { useToast } from '../../contexts/ToastContext';

interface DownloadOperation {
  id: number;
  customer_id: string;
  operation_name: string;
  operation_status: string;
  status_name: string;
  job_detail: {
    customer_account_id: string;
    platform_id: number;
    platform_username: string;
  };
  records_processed?: number;
  records_created?: number;
  records_updated?: number;
  error_message?: string;
  execution_time_ms?: number;
  started_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

const Downloads: React.FC = () => {
  const { currentCustomerId } = useSocialMediaStore();
  const { showError, showSuccess } = useToast();
  const [operations, setOperations] = useState<DownloadOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [retryingOperationId, setRetryingOperationId] = useState<number | null>(null);

  // Load operations when component mounts or customer changes
  useEffect(() => {
    if (currentCustomerId) {
      loadOperations();
    }
  }, [currentCustomerId]);

  const loadOperations = async () => {
    if (!currentCustomerId) return;

    setLoading(true);

    try {
      const response = await scrapingService.getCustomerOperations(currentCustomerId, {
        limit: 50,
        offset: 0
      });

      if (response.success && response.data) {
        setOperations(response.data);
      } else {
        showError('Failed to load operations', response.message || 'Unable to retrieve operations');
      }
    } catch (err) {
      showError('Failed to load operations', err instanceof Error ? err.message : 'An unexpected error occurred while loading operations');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (operation: DownloadOperation) => {
    setRetryingOperationId(operation.id);

    try {
      const response = await scrapingService.requestScraping({
        customer_id: operation.customer_id,
        customer_account_id: operation.job_detail.customer_account_id,
        platform_id: operation.job_detail.platform_id as 1 | 2,
        platform_username: operation.job_detail.platform_username
      });

      if (response.success) {
        showSuccess('Retry initiated', `Scraping operation has been restarted for @${operation.job_detail.platform_username}`);
        await loadOperations();
      } else {
        showError('Retry failed', response.message || 'Unable to restart scraping operation');
      }
    } catch (err) {
      showError('Retry failed', err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setRetryingOperationId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-3.5 w-3.5 text-red-500" />;
      case 'started':
      case 'running':
        return <Clock className="h-3.5 w-3.5 text-yellow-500 animate-pulse" />;
      default:
        return <Clock className="h-3.5 w-3.5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'started':
      case 'running':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!currentCustomerId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Jobs</h2>
        </div>
        <Card className="p-4">
          <div className="text-center py-6">
            <ListTodo className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-xs text-gray-500">Please select a customer to view scraping jobs.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Jobs</h2>
          <p className="text-xs text-gray-500 mt-0.5">Track your social media data scraping operations</p>
        </div>
        <button
          onClick={loadOperations}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-avi-primary text-white rounded-lg hover:bg-avi-primary/90 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading State */}
      {loading && operations.length === 0 && (
        <Card className="p-4">
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary mx-auto mb-3"></div>
            <p className="text-xs text-gray-500">Loading jobs...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!loading && operations.length === 0 && (
        <Card className="p-4">
          <div className="text-center py-6">
            <ListTodo className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">No Jobs</h3>
            <p className="text-xs text-gray-500 max-w-sm mx-auto">
              Start scraping new profiles from the Analysis tab to see them here.
            </p>
          </div>
        </Card>
      )}

      {/* Operations List */}
      {operations.length > 0 && (
        <div className="space-y-3">
          {operations.map((operation) => {
            const platformName = scrapingService.getPlatformName(operation.job_detail.platform_id as 1 | 2);

            return (
              <Card key={operation.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start space-x-3 min-w-0">
                    <div className="flex-shrink-0 mt-0.5">
                      {getStatusIcon(operation.operation_status)}
                    </div>
                    <div className="min-w-0">
                      {/* Username, platform, status */}
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h3 className="text-xs font-semibold text-gray-900">
                          @{operation.job_detail.platform_username}
                        </h3>
                        <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded capitalize">
                          {platformName}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded border ${getStatusColor(operation.operation_status)}`}>
                          {operation.status_name || operation.operation_status}
                        </span>
                      </div>

                      {/* Metadata row */}
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>Started: {formatDate(operation.started_at)}</span>
                        </div>

                        {operation.completed_at && (
                          <div className="flex items-center space-x-1">
                            <CheckCircle className="h-3 w-3" />
                            <span>Completed: {formatDate(operation.completed_at)}</span>
                          </div>
                        )}

                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>Duration: {formatDuration(operation.execution_time_ms)}</span>
                        </div>

                        {(operation.records_created ?? 0) > 0 && (
                          <div className="flex items-center space-x-1">
                            <User className="h-3 w-3" />
                            <span>{operation.records_created} records created</span>
                          </div>
                        )}
                      </div>

                      {/* Error message */}
                      {operation.error_message && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-xs text-red-700">{operation.error_message}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side: operation ID + retry */}
                  <div className="flex-shrink-0 text-right space-y-2">
                    <div className="text-xs text-gray-400">
                      <p>#{operation.id}</p>
                    </div>

                    {operation.operation_status.toLowerCase() === 'failed' && (
                      <button
                        onClick={() => handleRetry(operation)}
                        disabled={retryingOperationId === operation.id}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-avi-primary text-white rounded-lg hover:bg-avi-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${retryingOperationId === operation.id ? 'animate-spin' : ''}`} />
                        {retryingOperationId === operation.id ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Downloads;
